import { apiClient } from "../core/apiClient";

/**
 * Interfaz para los datos de un estudiante
 */
export interface Student {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  rut: string;
  telefono?: string;
  activo: boolean;
  created_at: string;
  updated_at: string;
  ticketBalance?: number;
}

/**
 * Interfaz para la respuesta de estudiantes vinculados
 */
export interface LinkedStudentsResponse {
  availablePacksToAssign: number;
  linkedStudents: Student[];
}

/**
 * Interfaz para crear un nuevo estudiante
 */
export interface CreateStudentRequest {
  nombre: string;
  apellido: string;
  rut: string;
  email?: string;
  telefono?: string;
  password: string;
}

/**
 * Interfaz para asignar tickets
 */
export interface AssignTicketsRequest {
  ticketPackId: string;
  quantity: number;
}

/**
 * Interfaz para canjear tickets
 */
export interface RedeemTicketRequest {
  redemptionDates: string[];
}

/**
 * Interfaz para la respuesta de canje de tickets
 */
export interface TicketRedemptionResponse {
  success: boolean;
  message: string;
  redeemedOrders?: any[];
  studentId?: string;
  redeemedBy?: string;
  error?: string;
}

/**
 * Interfaz para calcular días hábiles
 */
export interface CalculateBusinessDaysRequest {
  startDate: string;
  packSize: number;
}

/**
 * Interfaz para la respuesta de cálculo de días hábiles
 */
export interface BusinessDaysResponse {
  startDate: Date;
  actualStartDate: Date;
  packSize: number;
  businessDays: Date[];
  totalDays: number;
}

/**
 * Servicio para gestión de relaciones familiares
 * 
 * Maneja toda la comunicación con el backend para:
 * - Obtener estudiantes vinculados a un apoderado
 * - Registrar nuevos estudiantes
 * - Asignar tickets a estudiantes
 * - Canjear tickets por pedidos
 */
class FamilyService {
  private static readonly BASE_URL = "/api/v1/parents";

  /**
   * Obtiene los estudiantes vinculados a un apoderado
   * @param searchQuery - Término de búsqueda opcional
   * @returns Promise con los estudiantes vinculados y tickets disponibles
   */
  static async getLinkedStudents(
    searchQuery?: string
  ): Promise<LinkedStudentsResponse> {
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await apiClient.get<{ success: boolean; data: LinkedStudentsResponse }>(
        `${this.BASE_URL}/students`,
        { params }
      );

      return response.data.data;
    } catch (error: any) {
      this.handleError(error, "Error al obtener estudiantes vinculados");
      throw error;
    }
  }

  /**
   * Registra un nuevo estudiante y lo vincula al apoderado
   * @param studentData - Datos del estudiante a registrar
   * @returns Promise con el estudiante registrado
   */
  static async registerAndLinkStudent(
    studentData: CreateStudentRequest
  ): Promise<Student> {
    try {
      const response = await apiClient.post<{ success: boolean; data: Student }>(
        `${this.BASE_URL}/students`,
        studentData
      );

      return response.data.data;
    } catch (error: any) {
      this.handleError(error, "Error al registrar estudiante");
      throw error;
    }
  }

  /**
   * Asigna tickets a un estudiante
   * @param apoderadoId - ID del apoderado
   * @param studentId - ID del estudiante
   * @param data - Datos de la asignación
   * @returns Promise con el resultado de la asignación
   */
  static async assignTickets(
    // apoderadoId: string,
    studentId: string,
    data: AssignTicketsRequest
  ): Promise<{ message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${this.BASE_URL}/students/${studentId}/assign-tickets`,
        data
      );

      return { message: response.data.message };
    } catch (error: any) {
      this.handleError(error, "Error al asignar tickets");
      throw error;
    }
  }

  /**
   * Obtiene el balance de tickets de un estudiante
   * @param apoderadoId - ID del apoderado
   * @param studentId - ID del estudiante
   * @returns Promise con el balance de tickets
   */
  static async getStudentTicketBalance(
    // apoderadoId: string,
    studentId: string
  ): Promise<number> {
    try {
      const response = await apiClient.get<{ success: boolean; data: { balance: number } }>(
        `${this.BASE_URL}/students/${studentId}/ticket-balance`
      );

      return response.data.data.balance;
    } catch (error: any) {
      this.handleError(error, "Error al obtener balance de tickets");
      throw error;
    }
  }

  /**
   * Canjea tickets de un estudiante por pedidos
   * @param apoderadoId - ID del apoderado
   * @param studentId - ID del estudiante
   * @param data - Datos del canje
   * @returns Promise con el resultado del canje
   */
  static async redeemTicketForStudent(
    // apoderadoId: string,
    studentId: string,
    data: RedeemTicketRequest
  ): Promise<TicketRedemptionResponse> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string; data: any }>(
        `${this.BASE_URL}/students/${studentId}/redeem-ticket`,
        data
      );

      return {
        success: response.data.success,
        message: response.data.message,
        redeemedOrders: response.data.data?.redeemedOrders,
        studentId: response.data.data?.studentId,
        redeemedBy: response.data.data?.redeemedBy
      };
    } catch (error: any) {
      this.handleError(error, "Error al canjear tickets");
      throw error;
    }
  }

  /**
   * Obtiene los packs de tickets disponibles para un apoderado
   * @param apoderadoId - ID del apoderado
   * @returns Promise con los packs de tickets
   */
  static async getTicketPacks(
    // apoderadoId: string
  ): Promise<any[]> {
    try {
      const response = await apiClient.get<{ success: boolean; data: any[] }>(
        `${this.BASE_URL}/me/ticket-packs`
      );

      return response.data.data;
    } catch (error: any) {
      this.handleError(error, "Error al obtener packs de tickets");
      throw error;
    }
  }

  /**
   * Calcula días hábiles desde una fecha inicial
   * @param data - Datos para el cálculo (fecha inicial y tamaño del pack)
   * @returns Promise con los días hábiles calculados
   */
  static async calculateBusinessDays(
    data: CalculateBusinessDaysRequest
  ): Promise<BusinessDaysResponse> {
    try {
      const response = await apiClient.post<{ success: boolean; data: BusinessDaysResponse }>(
        `${this.BASE_URL}/calculate-business-days`,
        data
      );

      return response.data.data;
    } catch (error: any) {
      this.handleError(error, "Error al calcular días hábiles");
      throw error;
    }
  }

  /**
   * Programa un pack completo
   * @param packId - ID del pack a programar
   * @param startDate - Fecha inicial de programación
   * @param beneficiaryUserId - ID del usuario beneficiario
   * @returns Promise con el resultado de la programación
   */
  static async schedulePack(
    packId: string,
    startDate: string,
    beneficiaryUserId: string
  ): Promise<{ message: string }> {
    try {
      console.log('🚀 [FAMILY_SERVICE] Iniciando schedulePack:', {
        packId,
        startDate,
        beneficiaryUserId,
        url: `${this.BASE_URL}/schedule-pack`
      });

      const response = await apiClient.post<{ success: boolean; message: string }>(
        `${this.BASE_URL}/schedule-pack`,
        {
          packId,
          startDate,
          beneficiaryUserId
        },
        {
          timeout: 30000 // ✅ Aumentar timeout a 30 segundos para packs grandes
        }
      );

      console.log('✅ [FAMILY_SERVICE] schedulePack exitoso:', {
        success: response.data.success,
        message: response.data.message,
        status: response.status
      });

      return { message: response.data.message };
    } catch (error: any) {
      console.error('❌ [FAMILY_SERVICE] Error en schedulePack:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data,
        timeout: error.code === 'ECONNABORTED'
      });
      
      this.handleError(error, "Error al programar pack");
      throw error;
    }
  }

  /**
   * Maneja errores de la API de manera consistente
   * @param error - Error capturado
   * @param defaultMessage - Mensaje por defecto
   */
  private static handleError(error: any, _defaultMessage: string): void {
    console.error("FamilyService Error:", error);

    // Si es un error de respuesta del servidor
    if (error.response?.data) {
      const errorData = error.response.data;
      console.error("Server Error:", errorData.message, errorData.error);
    }

    // Si es un error de red
    if (error.code === "NETWORK_ERROR" || error.code === "ECONNABORTED") {
      console.error("Network Error:", error.message);
    }
  }

  /**
   * Verifica si un error es de autenticación
   * @param error - Error a verificar
   * @returns true si es un error de autenticación
   */
  static isAuthError(error: any): boolean {
    return error.response?.status === 401 || error.response?.status === 403;
  }

  /**
   * Verifica si un error es de validación
   * @param error - Error a verificar
   * @returns true si es un error de validación
   */
  static isValidationError(error: any): boolean {
    return error.response?.status === 400;
  }

  /**
   * Verifica si un error es de servidor
   * @param error - Error a verificar
   * @returns true si es un error de servidor
   */
  static isServerError(error: any): boolean {
    return error.response?.status >= 500;
  }

  /**
   * Obtiene el mensaje de error legible
   * @param error - Error a procesar
   * @param defaultMessage - Mensaje por defecto
   * @returns Mensaje de error legible
   */
  static getErrorMessage(error: any, defaultMessage: string): string {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    if (error.message) {
      return error.message;
    }

    return defaultMessage;
  }

  /**
   * Obtiene el historial de consumo del usuario (estudiante)
   * @param _userId - ID del usuario (estudiante)
   * @returns Promise con el historial de consumo
   */
  static async getMyConsumptionHistory(_userId: string): Promise<any[]> {
    try {
      const response = await apiClient.get<{ success: boolean; data: any[] }>(
        `/api/v1/users/me/consumption-history`
      );
      return response.data.data || [];
    } catch (error) {
      console.error('FamilyService Error:', error);
      throw error;
    }
  }

  /**
   * Obtiene los productos asignados del usuario (estudiante)
   * @param _userId - ID del usuario (estudiante)
   * @returns Promise con los productos asignados
   */
  static async getMyAssignedProducts(_userId: string): Promise<any[]> {
    try {
      const response = await apiClient.get<{ success: boolean; data: any[] }>(
        `/api/v1/users/me/assigned-products`
      );
      return response.data.data || [];
    } catch (error) {
      console.error('FamilyService Error:', error);
      throw error;
    }
  }
}

export default FamilyService; 
